import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalendarIcon,
  UserIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { investigationsService, CreateInvestigationData, InvestigationSubject } from '@/services/investigations.service';
import { SubjectCard } from '@/components/investigations/SubjectCard';
import { usersService, User } from '@/services/users.service';
import { USER_STORAGE_KEY } from '@/utils/constants';
import { SECURITY_CATEGORIES } from '@corporate-security/types';
import { DateInput } from '@/components/ui/DateInput';
import { toDateValue } from '@/utils/datetime';

// Component updated with Narrative field and Subject Information - v3
const CreateInvestigation: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [_narrativeLength, setNarrativeLength] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    narrative: '',
    priority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL',
    category: '',
    leadInvestigatorId: '',
    dueDate: '',
    relatedCases: [] as string[],
    subjects: [] as InvestigationSubject[],
  });

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await usersService.getUsers({ isActive: true });
        setUsers(response.users);

        // Get current user from localStorage and set as default lead investigator
        const currentUserStr = localStorage.getItem(USER_STORAGE_KEY);
        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            setFormData(prev => ({
              ...prev,
              leadInvestigatorId: currentUser.id
            }));
          } catch (err) {
            console.error('Failed to parse current user:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users. Please refresh the page.');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleInputChange = (field: string, value: string | string[]) => {
    if (field === 'narrative' && typeof value === 'string') {
      setNarrativeLength(value.length);
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Subject handlers for multiple subjects
  const handleSubjectChange = (index: number, field: keyof InvestigationSubject, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === index ? { ...subject, [field]: value } : subject
      ),
    }));
  };

  const handleAddSubject = () => {
    setFormData(prev => ({
      ...prev,
      subjects: [...prev.subjects, { isPrimary: prev.subjects.length === 0 }],
    }));
  };

  const handleRemoveSubject = (index: number) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.filter((_, i) => i !== index);
      // Ensure at least one subject is primary if any exist
      if (newSubjects.length > 0 && !newSubjects.some(s => s.isPrimary)) {
        newSubjects[0].isPrimary = true;
      }
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleSetPrimarySubject = (index: number) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => ({
        ...subject,
        isPrimary: i === index,
      })),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Strip HTML tags for validation
    const plainTextNarrative = formData.narrative.replace(/<[^>]*>/g, '').trim();
    if (!formData.title.trim() || !plainTextNarrative) {
      setError('Title and narrative are required');
      return;
    }

    if (!formData.leadInvestigatorId) {
      setError('Please select a lead investigator');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Find the selected user to build the leadInvestigator object
      const selectedUser = users.find(u => u.id === formData.leadInvestigatorId);
      if (!selectedUser) {
        setError('Selected lead investigator not found. Please try again.');
        setLoading(false);
        return;
      }

      // Filter subjects to only include those with content
      const subjectsWithContent = formData.subjects.filter(subject =>
        Boolean(subject.name) || Boolean(subject.email) || Boolean(subject.phone) || Boolean(subject.role) || Boolean(subject.department) || Boolean(subject.notes)
      ).map(subject => ({
        name: subject.name?.trim() ?? undefined,
        email: subject.email?.trim() ?? undefined,
        phone: subject.phone?.trim() ?? undefined,
        role: subject.role?.trim() ?? undefined,
        department: subject.department?.trim() ?? undefined,
        notes: subject.notes?.trim() ?? undefined,
        isPrimary: subject.isPrimary,
      }));

      const investigationData: CreateInvestigationData = {
        title: formData.title,
        narrative: formData.narrative,
        priority: formData.priority,
        category: formData.category,
        dueDate: formData.dueDate ?? undefined,
        relatedCases: formData.relatedCases,
        leadInvestigator: {
          id: selectedUser.id,
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          email: selectedUser.email,
        },
        subjects: subjectsWithContent.length > 0 ? subjectsWithContent : undefined,
      };

      await investigationsService.createInvestigation(investigationData);

      setSuccess(true);
      setTimeout(() => {
        navigate('/security/investigations');
      }, 2000);
    } catch (err) {
      console.error('Failed to create investigation:', err);
      setError(err instanceof Error ? err.message : 'Failed to create investigation');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/security/investigations');
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto mt-20 text-center"
      >
        <CheckCircleIcon className="w-16 h-16 text-success-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
          Investigation Created Successfully
        </h2>
        <p className="text-secondary-600 dark:text-secondary-400 mb-4">
          Your investigation case has been created and assigned. Redirecting to investigations list...
        </p>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
      </motion.div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeftIcon className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Create New Investigation
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Start a new investigation case with detailed information
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
            <span className="text-danger-700 dark:text-danger-300">{error}</span>
          </div>
        </motion.div>
      )}

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Investigation Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Investigation Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter a descriptive title for the investigation"
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                required
              />
            </div>

            {/* Narrative */}
            <RichTextEditor
              label="Narrative"
              required
              value={formData.narrative}
              onChange={(html) => handleInputChange('narrative', html)}
              placeholder="Provide a detailed narrative of the investigation including background, scope, objectives, and known facts"
              helpText="Include all relevant details about the investigation circumstances, background information, and preliminary findings"
              minHeight={200}
            />

            {/* Priority and Category Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Priority Level
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                >
                  <option value="LOW">Low Priority</option>
                  <option value="MEDIUM">Medium Priority</option>
                  <option value="HIGH">High Priority</option>
                  <option value="CRITICAL">Critical Priority</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                >
                  <option value="">Select Category</option>
                  {SECURITY_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Lead Investigator */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Lead Investigator *
              </label>
              {loadingUsers ? (
                <div className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-secondary-50 dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                  Loading users...
                </div>
              ) : (
                <select
                  value={formData.leadInvestigatorId ?? ''}
                  onChange={(e) => handleInputChange('leadInvestigatorId', e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  required
                  disabled={loadingUsers || users.length === 0}
                >
                  <option value="">Select Lead Investigator</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                The lead investigator will be responsible for managing this case
              </p>
            </div>

            {/* Subjects Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Subjects (Optional)</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddSubject}
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Subject
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                  Information about the person(s) or entity(ies) under investigation
                </p>
                {formData.subjects.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-secondary-200 dark:border-secondary-700 rounded-lg">
                    <UserIcon className="w-10 h-10 mx-auto text-secondary-400 mb-2" />
                    <p className="text-secondary-500 dark:text-secondary-400 mb-3">
                      No subjects added yet
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddSubject}
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Add First Subject
                    </Button>
                  </div>
                ) : (
                  <div>
                    {formData.subjects.map((subject, index) => (
                      <SubjectCard
                        key={index}
                        subject={subject}
                        index={index}
                        isEditing={true}
                        canDelete={true}
                        onChange={handleSubjectChange}
                        onDelete={handleRemoveSubject}
                        onSetPrimary={handleSetPrimarySubject}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Due Date */}
            <div>
              <DateInput
                label="Due Date (Optional)"
                value={formData.dueDate}
                onChange={(value) => handleInputChange('dueDate', value)}
                min={toDateValue(new Date())} // Prevent past dates
              />
            </div>

            {/* Related Cases */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                Related Case Numbers (Optional)
              </label>
              <input
                type="text"
                value={formData.relatedCases?.join(', ') ?? ''}
                onChange={(e) => handleInputChange('relatedCases', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="Enter related case numbers separated by commas (e.g., INV-0000001, INV-0000002)"
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              />
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                Link this investigation to existing cases for better correlation
              </p>
            </div>

            {/* Additional Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Created: {new Date().toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-sm text-secondary-600 dark:text-secondary-400">
                  <p>• A unique case number will be automatically generated</p>
                  <p>• Investigation status will be set to &quot;PENDING&quot; initially</p>
                  <p>• Team members can be assigned after creation</p>
                  <p>• Evidence and timeline events can be added during the investigation</p>
                </div>
              </CardContent>
            </Card>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.title.trim() || !formData.narrative.trim() || !formData.leadInvestigatorId}
                className="min-w-[120px]"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  'Create Investigation'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateInvestigation;